import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import {
  EDJ_QUICK_EDIT_EXTRA_FIELDS,
  EDJ_QUICK_PLAN_MESSAGES,
  EDJ_QUICK_PLAN_NET_WORTH_MAX,
  edjQuickPlanBirthDateProblem,
  isEdjQuickPlanMaritalCategory,
  isEdjQuickPlanSex,
  namedCensusRegionForZipCode,
  QUICK_EDIT_ALL_FIELDS,
  SessionType,
  type QuickEditFieldName,
} from '@shared';
import clientIntakeFormSchema from '@shared/utils/clientIntakeFormSchema.json';
import { selectSession } from '@client/model/selectors';
import {
  STATE_AUTOFILL_OPTIONS,
  STATE_ZIP_CODES,
  stateAutofillValueForZipCode,
} from '@client/util/stateZipCodes';
import {
  getQuickEditFieldError,
  isQuickEditFieldRequired,
  onlyDigitsKeyDown,
} from './quick-edit-field-validation';
import {
  formatQuickEditFieldValue,
  parseQuickEditFieldValue,
  QuickEditFieldInput,
} from './quick-edit-field-input';

const schema = clientIntakeFormSchema as Record<
  string,
  { type: string; label: string; group: string }
>;

const FULL_WIDTH_FIELDS = new Set<string>(['clientRace']);

const FIELD_GROUPS: { label: string; fields: QuickEditFieldName[] }[] = [
  {
    label: 'Contact',
    fields: ['clientFirstName', 'clientLastName', 'clientZipCode', 'clientLtcZipCode'],
  },
  {
    label: 'Demographic',
    fields: [
      'clientBirthDate',
      'clientGenderAtBirth',
      'clientRace',
      'householdHouseholdSize',
      'householdLivingChildrenCount',
      'clientLivingSiblingsCount',
    ],
  },
  {
    label: 'Health',
    fields: ['clientHeightInches', 'clientWeightPounds'],
  },
  {
    label: 'Financial',
    fields: ['clientDisabilityWorkersComp', 'householdTotalAssetsValue'],
  },
];

/** Marital status is editable only on an Edward Jones quick estimate (Married / Not married). */
type EditableFieldName = QuickEditFieldName | (typeof EDJ_QUICK_EDIT_EXTRA_FIELDS)[number];

/**
 * An Edward Jones quick estimate is built from DOB, sex, ZIP, Married / Not married and net worth.
 * Every other field would be saved but change nothing, so an EDJ client is only offered these,
 * plus the name and LTC ZIP that the standard form also groups here. Their rules are the shared
 * EDJ ones the server checks before saving (`edjQuickPlanEditProblem` in @shared).
 */
const EDJ_QUICK_PLAN_FIELD_GROUPS: { label: string; fields: EditableFieldName[] }[] = [
  {
    label: 'Contact',
    fields: ['clientFirstName', 'clientLastName', 'clientZipCode', 'clientLtcZipCode'],
  },
  {
    label: 'Demographic',
    fields: ['clientBirthDate', 'clientGenderAtBirth', 'clientMaritalStatus'],
  },
  {
    label: 'Financial',
    fields: ['householdTotalAssetsValue'],
  },
];

const EDJ_QUICK_PLAN_EDITABLE_FIELDS: readonly EditableFieldName[] =
  EDJ_QUICK_PLAN_FIELD_GROUPS.flatMap(group => group.fields);

export const QUICK_EDIT_FORM_ID = 'quick-edit-intake-form';

interface QuickEditFormProps {
  intakeSurvey: IntakeSurvey;
  initialValues?: Partial<Record<string, string | number | boolean | Date | null>>;
  /** Limits the form to the fields an Edward Jones quick estimate is built from. */
  edjQuickPlan?: boolean;
  onCancel: () => void;
  onSave: (fields: Record<string, string | number | null>) => void;
  /** Called instead of `onCancel` when saved with nothing changed, e.g. to retry a failed estimate. */
  onNoChanges?: () => void;
  onValidChange?: (valid: boolean) => void;
  onChangesChange?: (changes: QuickEditChange[]) => void;
}

export interface QuickEditChange {
  fieldName: string;
  label: string;
  before: string;
  after: string;
}

function formatChangeValue(fieldName: string, value: unknown): string {
  if (value == null || value === '') return 'Not provided';

  if (fieldName === 'clientHeightInches') {
    return formatQuickEditFieldValue(fieldName, value);
  }

  const fieldType = schema[fieldName]?.type;
  if (fieldType === 'date') {
    const date = new Date(value as string | number | Date);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        timeZone: 'UTC',
      });
    }
  }

  if (fieldType === 'integer' || fieldType === 'number') {
    const number = Number(value);
    if (Number.isFinite(number)) return number.toLocaleString('en-US');
  }

  return String(value);
}

function getFieldError(fieldName: string, value: string, edjQuickPlan = false): string | null {
  const standardProblem = getQuickEditFieldError(fieldName, value);
  if (standardProblem) return standardProblem;
  const edjProblem = edjQuickPlan ? getEdjFieldError(fieldName, value) : null;
  if (edjProblem) return edjProblem;
  return null;
}

/** The EDJ model input's rules, on top of the standard ones. */
function getEdjFieldError(fieldName: string, value: string): string | null {
  switch (fieldName) {
    case 'clientZipCode':
      return /^\d{5}$/.test(value) && !namedCensusRegionForZipCode(value)
        ? EDJ_QUICK_PLAN_MESSAGES.zipCode
        : null;
    case 'clientBirthDate':
      return edjQuickPlanBirthDateProblem(value);
    case 'clientGenderAtBirth':
      return isEdjQuickPlanSex(value) ? null : EDJ_QUICK_PLAN_MESSAGES.sex;
    case 'clientMaritalStatus':
      return isEdjQuickPlanMaritalCategory(value) ? null : EDJ_QUICK_PLAN_MESSAGES.maritalStatus;
    case 'householdTotalAssetsValue':
      return Number(value) > EDJ_QUICK_PLAN_NET_WORTH_MAX ? EDJ_QUICK_PLAN_MESSAGES.netWorth : null;
    default:
      return null;
  }
}

function FieldLabel({
  fieldName,
  label,
  htmlFor,
}: {
  fieldName: string;
  label: string;
  htmlFor: string;
}) {
  return (
    <Label htmlFor={htmlFor}>
      {label}
      {isQuickEditFieldRequired(fieldName) && <span className="ml-0.5 text-red-500">*</span>}
    </Label>
  );
}

function FieldError({ error }: { error: string | null }) {
  if (!error) return null;
  return <p className="text-xs text-red-500">{error}</p>;
}

export function QuickEditForm({
  intakeSurvey,
  initialValues,
  edjQuickPlan = false,
  onCancel,
  onSave,
  onNoChanges,
  onValidChange,
  onChangesChange,
}: QuickEditFormProps) {
  const { sessionType } = useSelector(selectSession);
  const isWizardSession = sessionType === SessionType.wizard;
  const fieldGroups = edjQuickPlan ? EDJ_QUICK_PLAN_FIELD_GROUPS : FIELD_GROUPS;
  // For EDJ only the fields shown are validated and saved, so hidden ones cannot block a save.
  const editableFields: readonly EditableFieldName[] = edjQuickPlan
    ? EDJ_QUICK_PLAN_EDITABLE_FIELDS
    : QUICK_EDIT_ALL_FIELDS;

  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const fieldName of editableFields) {
      const val =
        initialValues && Object.prototype.hasOwnProperty.call(initialValues, fieldName)
          ? initialValues[fieldName]
          : intakeSurvey[fieldName as keyof IntakeSurvey];
      if (val == null) {
        initial[fieldName] = '';
      } else if (fieldName === 'clientHeightInches') {
        initial[fieldName] = formatQuickEditFieldValue(fieldName, val);
      } else if (schema[fieldName]?.type === 'date') {
        const d = new Date(val as string);
        initial[fieldName] = isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
      } else {
        initial[fieldName] = String(val);
      }
    }
    return initial;
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const markTouched = useCallback((fieldName: string) => {
    setTouched(prev => (prev[fieldName] ? prev : { ...prev, [fieldName]: true }));
  }, []);

  const [hasDifferentLtcZip, setHasDifferentLtcZip] = useState<boolean>(() => {
    const zip = initialValues?.clientZipCode ?? intakeSurvey.clientZipCode;
    const ltcZip = initialValues?.clientLtcZipCode ?? intakeSurvey.clientLtcZipCode;
    return zip != null && ltcZip != null && String(zip) !== String(ltcZip);
  });

  const handleChange = (fieldName: string, value: string) => {
    setValues(prev => {
      const next = { ...prev, [fieldName]: value };
      if (fieldName === 'clientZipCode' && !hasDifferentLtcZip) {
        next.clientLtcZipCode = value;
      }
      return next;
    });
  };

  const handleLtcZipToggle = (checked: boolean) => {
    setHasDifferentLtcZip(checked);
    if (!checked) {
      setValues(prev => ({ ...prev, clientLtcZipCode: prev.clientZipCode }));
    }
  };

  const isFormValid = useMemo(() => {
    for (const fieldName of editableFields) {
      if (getFieldError(fieldName, values[fieldName] ?? '', edjQuickPlan)) return false;
    }
    return true;
  }, [values, editableFields, edjQuickPlan]);

  const proposedChanges = useMemo(() => {
    const fields: Record<string, string | number | null> = {};
    const changes: QuickEditChange[] = [];

    for (const fieldName of editableFields) {
      const original = intakeSurvey[fieldName as keyof IntakeSurvey];
      let originalStr = original != null ? String(original) : '';
      if (schema[fieldName]?.type === 'date' && original != null) {
        const date = new Date(original as string);
        originalStr = Number.isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
      } else if (fieldName === 'clientHeightInches') {
        originalStr = formatQuickEditFieldValue(fieldName, original);
      }

      const newValue = values[fieldName];
      if (newValue === originalStr) continue;

      const fieldSchema = schema[fieldName];
      if (!fieldSchema) continue;

      let persistedValue: string | number | null;
      if (newValue === '') {
        persistedValue = null;
      } else if (fieldName === 'clientHeightInches') {
        persistedValue = parseQuickEditFieldValue(fieldName, newValue);
      } else if (fieldSchema.type === 'integer') {
        persistedValue = parseInt(newValue, 10);
      } else if (fieldSchema.type === 'number') {
        persistedValue = parseFloat(newValue);
      } else {
        persistedValue = newValue;
      }

      fields[fieldName] = persistedValue;
      changes.push({
        fieldName,
        label: fieldSchema.label,
        before: formatChangeValue(fieldName, original),
        after: formatChangeValue(fieldName, persistedValue),
      });
    }

    return { fields, changes };
  }, [editableFields, intakeSurvey, values]);

  useEffect(() => {
    onValidChange?.(isFormValid);
  }, [isFormValid, onValidChange]);

  useEffect(() => {
    onChangesChange?.(proposedChanges.changes);
  }, [onChangesChange, proposedChanges.changes]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    if (proposedChanges.changes.length === 0) {
      (onNoChanges ?? onCancel)();
      return;
    }

    onSave(proposedChanges.fields);
  };

  const renderField = (fieldName: EditableFieldName) => {
    if (fieldName === 'clientLtcZipCode') return null;

    const fieldSchema = schema[fieldName];
    if (!fieldSchema) return null;

    const id = `quick-edit-${fieldName}`;
    const value = values[fieldName] ?? '';
    const isFullWidth = FULL_WIDTH_FIELDS.has(fieldName);
    const wrapperClass = isFullWidth ? 'space-y-1 sm:col-span-2' : 'space-y-1';
    const isTouched = touched[fieldName];
    const error = isTouched ? getFieldError(fieldName, value, edjQuickPlan) : null;

    if (fieldName === 'clientZipCode') {
      const ltcZipValue = values.clientLtcZipCode ?? '';
      const ltcTouched = touched.clientLtcZipCode;
      const zipError = isTouched ? getFieldError('clientZipCode', value, edjQuickPlan) : null;
      const ltcZipError = ltcTouched ? getFieldError('clientLtcZipCode', ltcZipValue) : null;
      const ltcFieldSchema = schema.clientLtcZipCode;

      const autofillState = stateAutofillValueForZipCode(String(value ?? ''));

      return (
        <div
          key={fieldName}
          className="space-y-4 sm:col-span-2"
        >
          {isWizardSession && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="quick-edit-stateZipAutofill">
                  Autofill zip code by state (admin only)
                </Label>
                <Select
                  value={autofillState}
                  onValueChange={abbr => {
                    handleChange('clientZipCode', STATE_ZIP_CODES[abbr]);
                    markTouched('clientZipCode');
                  }}
                >
                  <SelectTrigger
                    id="quick-edit-stateZipAutofill"
                    className="w-full min-w-0"
                  >
                    <SelectValue placeholder="Select a state" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATE_AUTOFILL_OPTIONS.map(({ abbr, name }) => (
                      <SelectItem
                        key={abbr}
                        value={abbr}
                      >
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <FieldLabel
                fieldName={fieldName}
                label={fieldSchema.label}
                htmlFor={id}
              />
              <Input
                id={id}
                type="text"
                value={value}
                onChange={e => handleChange(fieldName, e.target.value)}
                onBlur={() => markTouched(fieldName)}
                maxLength={5}
                minLength={5}
                inputMode="numeric"
                onKeyDown={onlyDigitsKeyDown}
              />
              <FieldError error={zipError} />
            </div>
            <div className="space-y-1">
              <FieldLabel
                fieldName="clientLtcZipCode"
                label={ltcFieldSchema?.label ?? 'Zip Code (during LTC)'}
                htmlFor="quick-edit-clientLtcZipCode"
              />
              <Input
                id="quick-edit-clientLtcZipCode"
                type="text"
                value={ltcZipValue}
                onChange={e => handleChange('clientLtcZipCode', e.target.value)}
                onBlur={() => markTouched('clientLtcZipCode')}
                disabled={!hasDifferentLtcZip}
                maxLength={5}
                minLength={5}
                inputMode="numeric"
                onKeyDown={onlyDigitsKeyDown}
              />
              <FieldError error={ltcZipError} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="quick-edit-hasDifferentLtcZip"
              checked={hasDifferentLtcZip}
              onCheckedChange={handleLtcZipToggle}
            />
            <Label
              htmlFor="quick-edit-hasDifferentLtcZip"
              className="text-sm font-normal"
            >
              Use a different zip code during LTC
            </Label>
          </div>
        </div>
      );
    }

    return (
      <div
        key={fieldName}
        className={wrapperClass}
      >
        <FieldLabel
          fieldName={fieldName}
          label={fieldSchema.label}
          htmlFor={id}
        />
        <QuickEditFieldInput
          fieldName={fieldName}
          id={id}
          value={value}
          onValueChange={nextValue => {
            handleChange(fieldName, nextValue);
            if (nextValue !== value) markTouched(fieldName);
          }}
          onBlur={() => markTouched(fieldName)}
          edjQuickPlan={edjQuickPlan}
        />
        <FieldError error={error} />
      </div>
    );
  };

  return (
    <form
      id={QUICK_EDIT_FORM_ID}
      onSubmit={handleSubmit}
      className="mt-6 space-y-8"
      data-hj-suppress
      data-private
    >
      {edjQuickPlan && (
        <p className="text-muted-foreground text-sm">
          This client has a quick estimate. Only the details it is built from can be edited here.
        </p>
      )}
      {fieldGroups.map(group => (
        <div key={group.label}>
          <h3 className="mb-4 text-sm font-semibold text-gray-700">{group.label}</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {group.fields.map(renderField)}
          </div>
        </div>
      ))}
    </form>
  );
}
